// src/components/reports/BaseReportPage.jsx
import { useState, useEffect, useRef } from 'react'
import { RefreshCw, FileText, Download, Printer } from 'lucide-react'
import { Button } from '../ui/Button'
import ReportsService from '../../services/reportsService'
import ReportSpecificFilters from './ReportSpecificFilters'
import { formatCurrency } from '../../utils/formatters'
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

const BaseReportPage = ({ 
  reportType, 
  title, 
  columns, 
  dataKey = 'data',
  enableFilters = true,
  customFilterComponent: CustomFilterComponent
}) => {


  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({})
  const [summary, setSummary] = useState(null)
  const printRef = useRef()

  // Load report data
  const loadData = async (currentFilters = filters) => {
    setLoading(true)
    try {
      console.log(`🔄 Loading ${reportType} report with filters:`, currentFilters)
      const result = await ReportsService.generateReport(reportType, currentFilters)
      
      console.log(`📊 ${reportType} service returned:`, result)
      
      const reportData = result[dataKey] || []
      console.log(`📊 Extracted ${reportType} data:`, reportData.length, 'records')
      
      setData(reportData)
      setFilteredData(reportData)
      setSummary(result.summary || null)
      
      console.log(`✅ ${reportType} report loaded:`, reportData.length, 'records')
    } catch (error) {
      console.error(`❌ Error loading ${reportType} report:`, error)
      setData([])
      setFilteredData([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    loadData()
  }, [reportType]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply filters for custom components with debouncing
  useEffect(() => {
    if (CustomFilterComponent && Object.keys(filters).length > 0) {
      // Debounce the filter application to prevent infinite loops
      const timeoutId = setTimeout(() => {
        console.log(`🔄 Auto-applying filters for ${reportType} (custom component)`)
        loadData(filters)
      }, 300) // 300ms debounce

      return () => clearTimeout(timeoutId)
    }
  }, [filters, CustomFilterComponent, reportType]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    console.log(`📝 ${reportType} filter change:`, newFilters)
    setFilters(newFilters)
    // Note: Auto-apply is now handled by the useEffect above
  }

  // Apply filters
  const handleApplyFilters = (filtersToApply = filters) => {
    console.log(`🔍 Applying filters for ${reportType}:`, filtersToApply)
    console.log('🔍 Current data before filtering:', data.length, 'records')
    loadData(filtersToApply)
  }

  // Clear filters
  const handleClearFilters = () => {
    console.log(`🗑️ Clearing filters for ${reportType}`)
    setFilters({})
    loadData({})
  }

  // Refresh data
  const handleRefresh = () => {
    console.log(`🔄 Refreshing ${reportType} report`)
    loadData()
  }

  // Export to Excel
  const exportToExcel = () => {
    try {
      const exportData = filteredData.length > 0 ? filteredData : data
      console.log(`📊 Exporting ${exportData.length} records to Excel for ${reportType}`)
      
      // Process data through renderCell for consistent formatting
      const processedData = exportData.map(row => {
        const processedRow = {}
        columns.forEach(col => {
          const value = row[col.key] || row[col.dataIndex] || ''
          const renderedValue = renderCell(value, col, true) // forExport = true
          const columnLabel = col.label || col.header || col.title || col.key
          processedRow[columnLabel] = renderedValue
        })
        return processedRow
      })
      
      const worksheet = XLSX.utils.json_to_sheet(processedData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, title)
      
      const fileName = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(workbook, fileName)
      
      console.log(`✅ Excel export completed: ${fileName}`)
    } catch (error) {
      console.error('❌ Error exporting to Excel:', error)
    }
  }

  // Export to PDF with automatic download
  const exportToPDF = () => {
    try {
      const exportData = filteredData.length > 0 ? filteredData : data
      console.log(`📄 Exporting ${exportData.length} records to PDF with automatic download`)
      
      if (exportData.length === 0) {
        console.warn('⚠️ No data to export')
        return
      }
      
      // Create PDF document in landscape mode for better column layout
      const doc = new jsPDF('landscape', 'pt', 'a4') // landscape, points, A4
      
      // Set document properties
      doc.setProperties({
        title: title,
        creator: 'Tezlaa Cafe Management System',
        author: 'Tezlaa System'
      })
      
      // Add title and header
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      const titleWidth = doc.getTextWidth(title)
      const pageWidth = doc.internal.pageSize.getWidth()
      doc.text(title, (pageWidth - titleWidth) / 2, 40)
      
      // Add generation date
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      const dateText = `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`
      const dateWidth = doc.getTextWidth(dateText)
      doc.text(dateText, (pageWidth - dateWidth) / 2, 60)
      
      // Add record count
      const recordText = `Records: ${exportData.length} ${Object.keys(filters).length > 0 ? '(Filtered)' : '(All)'}`
      const recordWidth = doc.getTextWidth(recordText)
      doc.text(recordText, (pageWidth - recordWidth) / 2, 75)
      
      // Add line under header
      doc.setLineWidth(1)
      doc.line(40, 85, pageWidth - 40, 85)
      
      let currentY = 100
      
      // Add filters summary if any
      if (Object.keys(filters).length > 0) {
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('Applied Filters:', 40, currentY)
        currentY += 20
        
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        Object.entries(filters).forEach(([key, value]) => {
          if (!value || value === 'all' || value === '') return
          let filterLabel = key
          if (key === 'startDate') filterLabel = 'Start Date'
          else if (key === 'endDate') filterLabel = 'End Date'
          else if (key === 'orderType') filterLabel = 'Order Type'
          else if (key === 'paymentType') filterLabel = 'Payment Type'
          else if (key === 'search' || key === 'customerName') filterLabel = 'Search'
          
          doc.text(`${filterLabel}: ${value}`, 50, currentY)
          currentY += 12
        })
        currentY += 10
      }
      
      // Add summary if provided
      if (summary) {
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('Summary:', 40, currentY)
        currentY += 20
        
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        if (summary.totalRevenue !== undefined) {
          doc.text(`Total Revenue: Rs. ${summary.totalRevenue.toFixed(2)}`, 50, currentY)
          currentY += 12
        }
        if (summary.totalOrders !== undefined) {
          doc.text(`Total Orders: ${summary.totalOrders}`, 50, currentY)
          currentY += 12
        }
        if (summary.avgOrderValue !== undefined) {
          doc.text(`Average Order Value: Rs. ${summary.avgOrderValue.toFixed(2)}`, 50, currentY)
          currentY += 12
        }
        currentY += 10
      }
      
      // Prepare table data
      const headers = columns.map(col => {
        const header = col.label || col.header || col.title || col.key || 'Column'
        return String(header).trim() || 'Column'
      })
      
      const tableData = exportData.map(row => 
        columns.map(col => {
          const value = row[col.key] || row[col.dataIndex] || ''
          // Use renderCell function for consistent formatting including custom renders
          const renderedValue = renderCell(value, col, true) // forExport = true
          return String(renderedValue || '')
        })
      )
      
      // Initialize variables for table positioning
      let yPos = currentY
      
      // Check if autoTable is available and use it, otherwise fallback to basic table
      if (typeof doc.autoTable === 'function') {
        console.log('✅ Using jsPDF autoTable plugin')
        
        // Enhanced table styling with autoTable
        doc.autoTable({
          head: [headers],
          body: tableData,
          startY: currentY,
          styles: { 
            fontSize: 8,
            cellPadding: 4,
            lineColor: [200, 200, 200],
            lineWidth: 0.5,
            textColor: [0, 0, 0]
          },
          headStyles: { 
            fillColor: [249, 115, 22], // Orange header
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center'
          },
          bodyStyles: {
            fillColor: [255, 255, 255]
          },
          alternateRowStyles: {
            fillColor: [249, 249, 249] // Light gray alternate rows
          },
          columnStyles: {
            // Apply column-specific styling based on column types
            ...Object.fromEntries(
              columns.map((col, index) => [
                index,
                {
                  halign: col.type === 'currency' || col.type === 'percentage' ? 'right' :
                         col.type === 'date' ? 'center' : 'left',
                  ...(col.type === 'currency' && { cellWidth: 'auto', minCellWidth: 50 })
                }
              ])
            )
          },
          margin: { top: 40, right: 40, bottom: 40, left: 40 },
          tableWidth: 'auto',
          theme: 'grid'
        })
      } else {
        console.log('⚠️ autoTable not available, using manual table layout')
        
        // Fallback: Create table manually with basic jsPDF functions
        doc.setFontSize(8)
        yPos = currentY
        const colWidth = (pageWidth - 80) / headers.length // Distribute columns evenly
        
        // Draw headers
        doc.setFont('helvetica', 'bold')
        doc.setFillColor(249, 115, 22) // Orange background
        doc.setTextColor(255, 255, 255) // White text
        
        headers.forEach((header, colIndex) => {
          const x = 40 + (colIndex * colWidth)
          
          // Draw background and border
          doc.setFillColor(249, 115, 22) // Orange background
          doc.rect(x, yPos, colWidth, 20, 'F') // Fill rectangle
          doc.setDrawColor(0, 0, 0) // Black border
          doc.rect(x, yPos, colWidth, 20, 'S') // Stroke rectangle
          
          // Set text properties
          doc.setTextColor(255, 255, 255) // White text
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(9)
          
          // Center text in cell
          const textWidth = doc.getTextWidth(header)
          const textX = x + (colWidth - textWidth) / 2
          const textY = yPos + 13
          
          doc.text(header, textX, textY)
        })
        
        yPos += 20
        
        // Draw data rows
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(0, 0, 0) // Black text
        
        tableData.forEach((row, rowIndex) => {
          // Alternate row colors
          if (rowIndex % 2 === 1) {
            doc.setFillColor(249, 249, 249) // Light gray
            doc.rect(40, yPos, pageWidth - 80, 15, 'F')
          }
          
          row.forEach((cell, colIndex) => {
            const x = 40 + (colIndex * colWidth)
            doc.rect(x, yPos, colWidth, 15, 'S') // Draw cell border
            
            // Align text based on column type
            const col = columns[colIndex]
            let textX = x + 5 // Default left align with padding
            
            if (col && (col.type === 'currency' || col.type === 'percentage')) {
              // Right align
              const textWidth = doc.getTextWidth(String(cell))
              textX = x + colWidth - textWidth - 5
            } else if (col && col.type === 'date') {
              // Center align
              const textWidth = doc.getTextWidth(String(cell))
              textX = x + (colWidth - textWidth) / 2
            }
            
            doc.text(String(cell || ''), textX, yPos + 10)
          })
          
          yPos += 15
          
          // Add new page if needed
          if (yPos > doc.internal.pageSize.getHeight() - 100) {
            doc.addPage()
            yPos = 40
          }
        })
      }
      
      // Add footer - calculate position based on whether autoTable was used
      let finalY
      if (typeof doc.autoTable === 'function' && doc.lastAutoTable) {
        finalY = doc.lastAutoTable.finalY + 20
      } else {
        // If manual table was used, yPos is the current position
        finalY = yPos + 20
      }
      
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(0, 0, 0)
      const footerText = `Total Records: ${exportData.length} | Generated from Tezlaa Cafe Management System`
      doc.text(footerText, 40, finalY)
      
      // Generate filename and download
      const fileName = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
      
      console.log(`✅ PDF automatically downloaded: ${fileName}`)
    } catch (error) {
      console.error('❌ Error exporting to PDF:', error)
    }
  }

  // Print function with HTML table format
  const handlePrint = () => {
    try {
      console.log(`🖨️ Printing ${reportType} report as HTML table`)
      
      const printData = filteredData.length > 0 ? filteredData : data
      
      // Create print window content
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #000;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 15px;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              color: #333;
            }
            .header .date {
              margin: 5px 0 0 0;
              font-size: 12px;
              color: #666;
            }
            .summary {
              margin-bottom: 20px;
              padding: 10px;
              background: #f5f5f5;
              border-radius: 4px;
            }
            .summary h3 {
              margin: 0 0 10px 0;
              font-size: 16px;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f97316;
              color: white;
              font-weight: bold;
            }
            tbody tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            tbody tr:hover {
              background-color: #f5f5f5;
            }
            .text-right {
              text-align: right;
            }
            .text-center {
              text-align: center;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .header { page-break-inside: avoid; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${title}</h1>
            <p class="date">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            <p class="date">Records: ${printData.length} ${Object.keys(filters).length > 0 ? '(Filtered)' : '(All)'}</p>
          </div>
          
          ${Object.keys(filters).length > 0 ? `
          <div class="summary">
            <h3>Applied Filters</h3>
            ${Object.entries(filters).map(([key, value]) => {
              if (!value || value === 'all' || value === '') return ''
              let filterLabel = key
              if (key === 'startDate') filterLabel = 'Start Date'
              else if (key === 'endDate') filterLabel = 'End Date'
              else if (key === 'orderType') filterLabel = 'Order Type'
              else if (key === 'paymentType') filterLabel = 'Payment Type'
              else if (key === 'search' || key === 'customerName') filterLabel = 'Search'
              
              return `<div class="summary-row"><span>${filterLabel}:</span><span>${value}</span></div>`
            }).join('')}
          </div>
          ` : ''}
          
          ${summary ? `
          <div class="summary">
            <h3>Summary</h3>
            ${summary.totalRevenue !== undefined ? `<div class="summary-row"><span>Total Revenue:</span><span>Rs. ${summary.totalRevenue.toFixed(2)}</span></div>` : ''}
            ${summary.totalOrders !== undefined ? `<div class="summary-row"><span>Total Orders:</span><span>${summary.totalOrders}</span></div>` : ''}
            ${summary.avgOrderValue !== undefined ? `<div class="summary-row"><span>Average Order Value:</span><span>Rs. ${summary.avgOrderValue.toFixed(2)}</span></div>` : ''}
          </div>
          ` : ''}
          
          <table>
            <thead>
              <tr>
                ${columns.map(col => `<th class="${col.type === 'currency' ? 'text-right' : col.type === 'date' ? 'text-center' : ''}">${col.label || col.header || col.title || col.key || 'Column'}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${printData.map(row => `
                <tr>
                  ${columns.map(col => {
                    const value = row[col.key] || ''
                    // Use renderCell function for consistent formatting including custom renders
                    const formattedValue = renderCell(value, col, true) // forExport = true for print
                    let cellClass = ''
                    
                    if (col.type === 'currency') {
                      cellClass = 'text-right'
                    } else if (col.type === 'date') {
                      cellClass = 'text-center'
                    } else if (col.type === 'percentage') {
                      cellClass = 'text-right'
                    }
                    
                    return `<td class="${cellClass}">${formattedValue}</td>`
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Total Records: ${printData.length} | Printed from Tezlaa Cafe Management System</p>
          </div>
        </body>
        </html>
      `
      
      // Open new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600')
      printWindow.document.write(printContent)
      printWindow.document.close()
      
      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print()
          // Close the print window after printing
          printWindow.onafterprint = () => {
            printWindow.close()
          }
        }, 500)
      }
      
    } catch (error) {
      console.error('❌ Error printing:', error)
    }
  }

  // Render table cell with proper formatting
  const renderCell = (value, column, forExport = false) => {
    // Check if column has custom render function
    if (column.render && typeof column.render === 'function') {
      if (forExport) {
        // For exports, if the render function returns JSX, we need the plain text
        // For status-like columns, just return the original value
        if (column.key === 'status' || column.label === 'Payment Status') {
          return value // Return the original status text
        }
        // For other custom renders that might return strings, try to use them
        const rendered = column.render(value)
        return typeof rendered === 'string' ? rendered : value
      } else {
        // For UI display, use the custom render function as-is
        return column.render(value)
      }
    }
    
    // Handle null or undefined values
    if (value === null || value === undefined) {
      return '-'
    }
    
    // Handle object values (prevent React errors when trying to render objects)
    if (typeof value === 'object' && value !== null) {
      // If it's an object with a name property, return that
      if (value.name) {
        return value.name
      }
      // If it's a date object
      if (value instanceof Date) {
        return value.toLocaleDateString()
      }
      // Otherwise, return a string representation or default
      return JSON.stringify(value)
    }
    
    if (column.type === 'currency') {
      return formatCurrency(value)
    }
    if (column.type === 'date') {
      return new Date(value).toLocaleDateString()
    }
    if (column.type === 'percentage') {
      return `${parseFloat(value || 0).toFixed(2)}%`
    }
    if (column.type === 'status') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value === 'Active' || value === 'In Stock' 
            ? 'bg-green-100 text-green-800' 
            : value === 'Inactive' || value === 'Low Stock' || value === 'Out of Stock'
            ? 'bg-red-100 text-red-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {value}
        </span>
      )
    }
    return value || 'N/A'
  }

  return (
    <div className="space-y-6">
      {/* Header with Action Buttons */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <p className="text-gray-600 mt-1">
              {filteredData.length > 0 ? filteredData.length : data.length} records
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <Button
              onClick={handleRefresh}
              disabled={loading}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
            
            <Button
              onClick={exportToPDF}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span>PDF</span>
            </Button>
            
            <Button
              onClick={exportToExcel}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Excel</span>
            </Button>
            
            <Button
              onClick={handlePrint}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
            >
              <Printer className="h-4 w-4" />
              <span>Print</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        {enableFilters && (
          CustomFilterComponent ? (
            <CustomFilterComponent
              filters={filters}
              onFiltersChange={handleFilterChange}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />
          ) : (
            <ReportSpecificFilters
              reportType={reportType}
              filters={filters}
              onFilterChange={handleFilterChange}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />
          )
        )}
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border" ref={printRef}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
            <span className="ml-2 text-gray-600">Loading report data...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key || column.dataIndex}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {column.label || column.header || column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(filteredData.length > 0 ? filteredData : data).map((row, index) => (
                  <tr key={row.id || index} className="hover:bg-gray-50">
                    {columns.map((column) => (
                      <td
                        key={column.key || column.dataIndex}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                      >
                        {renderCell(row[column.key || column.dataIndex], column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* No Data Message */}
            {data.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No data available</p>
                <p className="text-gray-400 text-sm mt-2">Try adjusting your filters or refresh the data</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default BaseReportPage